import styleSwitcherControl from "./styleSwitcherControl";
import turfEqual from "@turf/boolean-equal";
import tokml from "tokml";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

import MaplibreTextStampControl from "./TextControls/MaplibreTextStampControl";
import MaplibreTextControl from "./TextControls/MaplibreTextControl";
import { StaticMode } from "./MapHelpers";
import GeocoderApi from "./geocoderApi";
import {
    debounce,
    getPointForSnap,
    getPolygonBounds,
    getPolygonBoundsWithBearing,
    splitFeature,
    getPolygonBoundsAndZoom
} from "../../model/utils";
import { linesImage } from "./ShadeImage";
import wardControl from "./wardControl";
import PostcodesService from "../../model/postcodesService";

const maplibregl = window.maplibregl;
const MapboxDraw = window.MapboxDraw;
const MaplibreGeocoder = window.MaplibreGeocoder;

const polygonDefaultStyle = {
    layout: {},
    paint: {
        "fill-opacity": 0,
        "fill-pattern": "pattern"
    }
};

const roadsDefaultStyle = {
    layout: {
        "line-join": "round",
        "line-cap": "round"
    },
    paint: {
        "line-color": ["case", ["boolean", ["feature-state", "selected"], false], "#00ff00", "#9daaff"],
        "line-width": 6,
        "line-opacity": 0.5
    }
};

class MapEngine {
    constructor(container, model) {
        this.counter = 0;
        this.container = container;
        this.model = model;
        this.prepareMap();
        this.roads = {};
        this.selected = {};
        this.snapMarker = null;

        this.householdsPopup = new maplibregl.Popup({ closeOnClick: true });

        //
        this.selectClickHandlerBinded = this.selectClickHandler.bind(this);
        this.enterClickHandlerBinded = this.enterClickHandler.bind(this);
        this.leaveClickHandlerBinded = this.leaveClickHandler.bind(this);
        this.selectPolygonForExportPDFBinded = this.selectPolygonForExportPDF.bind(this);
    }

    prepareMap() {
        const baseMaps = {
            osm: {
                name: "OpenStreetMap",
                img: "https://cloud.maptiler.com/static/img/maps/streets.png"
            },
            satellite: {
                name: "Satellite",
                img: "https://cloud.maptiler.com/static/img/maps/hybrid.png"
            }
        };

        this.map = new maplibregl.Map({
            container: this.container,
            style: {
                version: 8,
                glyphs: "./open-sans.pbf?{fontstack},{range}",
                // glyphs: "http://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
                // glyphs: "http://fonts.openmaptiles.org/Open%20Sans%20Regular/0-255.pbf?{fontstack},{range}",
                sources: {
                    osm: {
                        type: "raster",
                        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
                        tileSize: 256,
                        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    },
                    satellite: {
                        type: "raster",
                        tiles: [
                            "https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v10/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZmx5ZXJib3giLCJhIjoiY2p6NG1ubmFrMDhkeDNkbXE4NDZmbzI3ZSJ9.QmNNAF_07wCNhJZ35XhoCg"
                        ],
                        tileSize: 512,
                        attribution:
                            'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
                        id: "mapbox/satellite-v9",
                        zoomOffset: -1
                    }
                },
                layers: [
                    {
                        id: "osm",
                        type: "raster",
                        source: "osm",
                        minzoom: 0,
                        maxzoom: 22
                    },
                    {
                        id: "satellite",
                        type: "raster",
                        source: "satellite",
                        minzoom: 0,
                        maxzoom: 19,
                        layout: {
                            visibility: "none"
                        }
                    }
                ]
            },
            center: [-0.289285, 51.420323],
            zoom: 15,
            preserveDrawingBuffer: true
        });

        //
        window.map = this.map;

        //
        this.map.loadImage(linesImage, (err, image) => {
            if (!err) {
                this.map.addImage("pattern", image);
            }
        });

        // search
        this.map.addControl(
            new MaplibreGeocoder(GeocoderApi, {
                maplibregl: maplibregl,
                // flyTo: false,
                showResultsWhileTyping: true
            })
        );

        this.map.addControl(new wardControl({ model: this.model }), "top-right");

        this.map.addControl(new maplibregl.NavigationControl(), "top-right");

        this.map.addControl(new styleSwitcherControl({ basemaps: baseMaps, initialBasemap: "osm" }), "bottom-left");

        // draw
        const modes = MapboxDraw.modes;
        modes.static = StaticMode;
        this.drawControl = new MapboxDraw({
            displayControlsDefault: false,
            controls: {},
            modes
        });
        this.map.addControl(this.drawControl);

        // window.map = this.map;
        // window.drawControl = this.drawControl;

        this.polygons = {
            type: "FeatureCollection",
            features: [],
            id: "polygons"
        };

        // layers
        this.map.once("load", this.initLayers.bind(this));

        this.map.on("mousemove", debounce(this.splitRoadsMoveHandler.bind(this), 5));
        this.map.on("click", this.splitRoadsClicked.bind(this));
        this.map.on("draw.create", this.itemCreated.bind(this));
        this.map.on("draw.update", this.polygonsChanged.bind(this));
        this.map.on("click", "polygons", this.polygonClickedHaldler.bind(this));
    }

    initLayers() {
        this.polygonSource = this.map.addSource("polygons", {
            type: "geojson",
            data: this.polygons
        });

        this.map.addLayer({
            id: "polygons",
            type: "fill",
            source: "polygons",
            ...polygonDefaultStyle
        });

        this.map.addLayer({
            id: "polygons-line",
            type: "line",
            source: "polygons",
            layout: {
                "line-join": "round",
                "line-cap": "round"
            },
            paint: {
                "line-color": "#000000",
                "line-width": 2,
                "line-opacity": 1
            }
        });

        this.map.addSource("snapMarker", {
            type: "geojson",
            data: null
        });

        this.map.addLayer({
            id: "snapMarker",
            type: "circle",
            source: "snapMarker",
            paint: {
                "circle-radius": 5,
                "circle-color": "#ffffff",
                "circle-stroke-color": "#000",
                "circle-stroke-width": 2
            }
        });

        this.stampControl = new MaplibreTextStampControl();
        this.map.addControl(this.stampControl);

        this.textControl = new MaplibreTextControl("tc-text");
        this.map.addControl(this.textControl);

        this.startControl = new MaplibreTextControl("tc-start", "x\n");
        this.map.addControl(this.startControl);
    }

    async addRoadToPolygon(polygon, layerId) {
        const roadsData = await this.model.polygonCreated(polygon, layerId);
        roadsData.features.forEach((item, i) => {
            item.related = layerId;
            item._id = item.id;
            item.id = +layerId * 10000 + i;
        });
        this.roads[layerId] = roadsData;

        const layerName = `roads${layerId}`;

        const source = this.map.getSource(layerName);

        if (source) {
            source.setData(this.roads[layerId]);
        } else {
            this.map.addSource(layerName, {
                type: "geojson",
                data: this.roads[layerId]
            });

            this.map.addLayer(
                {
                    id: layerName,
                    type: "line",
                    source: layerName,
                    ...roadsDefaultStyle
                },
                "polygons"
            );
        }
    }

    async polygonCreated(feature) {
        feature.layerId = this.counter++;
        feature.id = feature.layerId;
        feature.properties.id = feature.layerId;
        this.polygons.features.push(feature);
        this.map.getSource("polygons").setData(this.polygons);
        this.drawControl.deleteAll();

        await this.addRoadToPolygon(feature, feature.layerId);
        this.calculateTotalHouseholds();
    }

    async itemCreated(e) {
        if (!this.model.drawing && !this.model.addRoad) {
            return;
        }

        const feature = e.features[0];
        if (feature.geometry.type === "Polygon") {
            this.polygonCreated(feature);
        } else {
            this.lineCreated(feature);
        }
    }

    async calculateTotalHouseholds() {
        let count = 0;

        for await (const item of this.polygons.features) {
            const households = await PostcodesService.calculateHouseholds(item);
            count += households;
        }

        this.model.setTotalHouseholds(count);
    }

    startDrawing() {
        this.drawControl.changeMode("draw_polygon");
        return {};
    }

    stopDrawing() {
        this.drawControl.changeMode("static");
        this.drawControl.deleteAll();
    }

    startModify() {
        this.map.setLayoutProperty("polygons-line", "visibility", "none");
        this.drawControl.add(this.polygons);
        this.drawControl.changeMode("simple_select");
        return this.polygons.features;
    }

    polygonsChanged(e) {
        this.model.changeModify(e.features);
    }

    async saveModify(editing) {
        // logObject(editing);

        for (let i = 0; i < this.polygons.features.length; i++) {
            if (!turfEqual(this.polygons.features[i], editing[i])) {
                this.polygons.features[i] = {
                    ...editing[i],
                    layerId: this.polygons.features[i].layerId
                };
                await this.addRoadToPolygon(editing[i], this.polygons.features[i].layerId);
            }
        }

        this.map.getSource("polygons").setData(this.polygons);

        this.drawControl.deleteAll();
        this.drawControl.changeMode("static");
        this.map.setLayoutProperty("polygons-line", "visibility", "visible");

        this.calculateTotalHouseholds();
    }

    cancelModify() {
        this.map.setLayoutProperty("polygons-line", "visibility", "visible");
        this.drawControl.deleteAll();
        this.drawControl.changeMode("static");
    }

    getRoadsColor(layerId) {
        return this.map.getPaintProperty(`roads${layerId}`, "line-color")[3];
    }

    changeRoadsColor(layerId, color) {
        this.map.setPaintProperty(`roads${layerId}`, "line-color", [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            "#00ff00",
            color
        ]);
    }

    startAddRoad() {
        this.drawControl.changeMode("draw_line_string");
        return {};
    }

    stopAddRoad() {
        this.drawControl.deleteAll();
        this.drawControl.changeMode("static");
    }

    lineCreated(line) {
        this.model.addRoadsFromLine(line, this.polygons.features);
    }

    addRoads(layerId, roads) {
        let maxId = 0;
        this.roads[layerId].features.forEach((item) => {
            if (item.id > maxId) {
                maxId = item.id;
            }
        });

        roads.features.forEach((item, index) => {
            item.id = maxId + 1 + index;
            this.roads[layerId].features.push(item);
        });

        const layerName = `roads${layerId}`;
        const source = this.map.getSource(layerName);
        source.setData(this.roads[layerId]);

        this.drawControl.deleteAll();
        this.drawControl.changeMode("static");
    }

    selectClickHandler(e) {
        const roadFeature = this.map.queryRenderedFeatures(e.point).find((item) => item.layer.id.startsWith("roads"));

        if (roadFeature) {
            const feature = { source: roadFeature.layer.id, id: roadFeature.id };
            const state = this.map.getFeatureState(feature);
            if (state.selected) {
                this.map.setFeatureState(feature, { selected: false });
                delete this.selected[roadFeature.id];
            } else {
                this.map.setFeatureState(feature, { selected: true });
                this.selected[roadFeature.id] = +roadFeature.layer.id.substr(5);
            }
        }
    }

    enterClickHandler() {
        this.map.getCanvas().style.cursor = "pointer";
    }

    leaveClickHandler() {
        this.map.getCanvas().style.cursor = "";
    }

    startSelectRoads() {
        this.map.on("click", this.selectClickHandlerBinded);

        for (let i = 0; i < this.counter; i++) {
            const layerName = `roads${i}`;

            this.map.on("mouseenter", layerName, this.enterClickHandlerBinded);
            this.map.on("mouseleave", layerName, this.leaveClickHandlerBinded);
        }

        return {};
    }

    deleteSelectedRoads() {
        for (let id in this.selected) {
            const layerId = this.selected[id];
            const index = this.roads[layerId].features.findIndex((item) => item.id === +id);
            if (index > -1) {
                this.roads[layerId].features.splice(index, 1);
            }

            const layerName = `roads${layerId}`;
            const source = this.map.getSource(layerName);
            if (source) {
                source.setData(this.roads[layerId]);
            }
        }
    }

    endSelectRoads() {
        this.map.off("click", this.selectClickHandlerBinded);

        for (let i = 0; i < this.counter; i++) {
            const layerName = `roads${i}`;

            this.map.off("mouseenter", layerName, this.enterClickHandlerBinded);
            this.map.off("mouseleave", layerName, this.leaveClickHandlerBinded);

            this.map.removeFeatureState({
                source: layerName
            });

            this.selected = {};
        }
    }

    getAllRoads() {
        const roads = [];

        Object.values(this.roads).forEach((item) => roads.push(...item.features));

        return roads;
    }

    splitRoadsMoveHandler(e) {
        if (!this.model.splitRoads) {
            return;
        }

        const features = this.getAllRoads();

        const { point, related, index } = getPointForSnap([e.lngLat.lng, e.lngLat.lat], features);

        if (point) {
            this.map.getSource("snapMarker").setData({
                type: "Feature",
                properties: {},
                geometry: {
                    coordinates: [point[1], point[0]],
                    type: "Point"
                }
            });
            this.snapMarker = { related, index, point };
        } else {
            this.map.getSource("snapMarker").setData({
                type: "FeatureCollection",
                features: []
            });
            this.snapMarker = null;
        }
    }

    splitRoadsClicked(e) {
        if (!this.snapMarker) {
            return;
        }

        const { related, index, point } = this.snapMarker;

        const features = this.getAllRoads();
        const road = features[index];

        const splitted = splitFeature(road, [point[1], point[0]]);

        const roads = this.roads[related];
        const deleteIndex = roads.features.findIndex((item) => item.id === road.id);
        if (deleteIndex > -1) {
            roads.features.splice(deleteIndex, 1);
        }

        this.addRoads(related, splitted);
    }

    endSplitRoads() {
        this.map.getSource("snapMarker").setData({
            type: "FeatureCollection",
            features: []
        });
        this.snapMarker = null;
    }

    startDelete() {
        this.map.setPaintProperty("polygons", "fill-opacity", 0.75);
    }

    endDelete() {
        this.map.setPaintProperty("polygons", "fill-opacity", 0);
    }

    polygonClickedHaldler(e) {
        if (this.model.delete) {
            this.deleteClickedHandler(e);
        } else if (!this.model.exportPDF) {
            // if (window.location.href.includes("craft")) {
                this.householdsPopup
                    .setLngLat(e.lngLat)
                    .setHTML(`<h3 style="margin: 0;">Calculation...</h3>`)
                    .addTo(this.map);

                PostcodesService.calculateHouseholds(e.features[0]).then((households) => {
                    this.householdsPopup.setHTML(`<h3 style="margin: 0;">Total Households: ${households}</h3>`);
                });
            // }
        }
    }

    deleteClickedHandler(e) {
        if (this.model.delete) {
            const polygon = this.map.queryRenderedFeatures(e.point).find((item) => item.layer.id === "polygons");
            if (polygon && typeof polygon.id !== "undefined") {
                const index = this.polygons.features.findIndex((item) => item.id === polygon.id);
                if (index > -1) {
                    const layerName = `roads${polygon.id}`;
                    this.map.removeLayer(layerName);
                    this.map.removeSource(layerName);

                    this.model.deletePolygon(polygon.id);

                    this.polygons.features.splice(index, 1);
                    this.map.getSource("polygons").setData(this.polygons);

                    this.calculateTotalHouseholds();
                }
            }
        }
    }

    exportKML() {
        const features = this.getAllRoads();

        const kmlColor = "#faaa03";

        features.forEach((item) => {
            item.id = item._id;
            item.properties.stroke = kmlColor;
            item.properties["stroke-width"] = 3;
        });

        if (features.length) {
            const kml = tokml(
                { type: "FeatureCollection", features },
                {
                    name: "name",
                    simplestyle: true
                }
            );

            var blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml;charset=utf-8" });
            saveAs(blob, "Roads.kml");
        }
    }

    //
    nextShade(layerId) {
        this.map.setPaintProperty("polygons", "fill-opacity", ["match", ["get", "id"], layerId, 0, 0.7]);
    }

    stopShading() {
        this.map.setPaintProperty("polygons", "fill-opacity", 0);
    }

    //
    addStamp(text) {
        this.stampControl.addItem(text);
    }

    addText() {
        this.textControl.addItem("text");
    }

    addStart() {
        this.startControl.addItem("Start");
    }

    async selectPolygonForExportPDF(e) {
        const polygon = this.map.queryRenderedFeatures(e.point).find((item) => item.layer.id === "polygons");
        if (polygon && typeof polygon.id !== "undefined") {
            await this.exportPDF(polygon.id);
        }
    }

    startExportPDF() {
        this.map.on("click", "polygons", this.selectPolygonForExportPDFBinded);

        return {};
    }

    stopExportPDF() {
        this.map.off("click", "polygons", this.selectPolygonForExportPDFBinded);
    }

    // export PDF
    async exportPDF(num) {
        const initialState = {
            center: this.map.getCenter(),
            zoom: this.map.getZoom(),
            bearing: this.map.getBearing(),
            pitch: this.map.getPitch()
        };

        const polygon = this.polygons.features[num];
        const { center, zoom } = getPolygonBoundsAndZoom(polygon, this.map);

        this.map.flyTo({
            center: center,
            zoom: zoom,
            bearing: this.map.getBearing(),
            pitch: this.map.getPitch(),
            speed: 1.2,
            curve: 1.42
        });

        this.map.once("idle", () => {
            const canvas = this.map.getCanvas();
            canvas.toBlob((blob) => {
                const img = URL.createObjectURL(blob);

                const tempPdf = new jsPDF();
                const imgProperties = tempPdf.getImageProperties(img);

                const imgRatio = imgProperties.width / imgProperties.height;

                const pdf = new jsPDF({
                    orientation: imgRatio > 1 ? "landscape" : "portrait"
                });

                const margin = 15; // mm

                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const contentWidth = pageWidth - 2 * margin;
                const contentHeight = pageHeight - 2 * margin;

                const contentRatio = contentWidth / contentHeight;

                let finalWidth, finalHeight;
                if (imgRatio > contentRatio) {
                    finalWidth = contentWidth;
                    finalHeight = contentWidth / imgRatio;
                } else {
                    finalHeight = contentHeight;
                    finalWidth = contentHeight * imgRatio;
                }

                const xOffset = margin + (contentWidth - finalWidth) / 2;
                const yOffset = margin + (contentHeight - finalHeight) / 2;

                pdf.addImage(img, "PNG", xOffset, yOffset, finalWidth, finalHeight);
                pdf.save("map.pdf");

                // Восстанавливаем исходное состояние карты
                this.map.flyTo({
                    center: initialState.center,
                    zoom: initialState.zoom,
                    bearing: initialState.bearing,
                    pitch: initialState.pitch,
                    speed: 1.2,
                    curve: 1.42
                });
            });
        });
    }
}

export default MapEngine;
